import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { products } from '../../data/products';
import ProductImageGallery from '../../components/ProductImageGallery';
import { useCart } from '../../context/CartContext';

const X7MGameboy = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [activeTab, setActiveTab] = useState('overview');
  const [currentImage, setCurrentImage] = useState('');
  const [selectedColor, setSelectedColor] = useState('Red & Blue');
  const [selectedPlayer, setSelectedPlayer] = useState('Single Player');
  const [quantity, setQuantity] = useState(1);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  
  // 获取游戏机产品数据
  const gameboy = products.find(p => p.name === 'X7M Retro Handheld Game Console – SUP Style, 500 Classic Games, Big Screen, TV Output');
  
  // 更多图片 - 使用正方形图片
  const additionalImages = [
    gameboy.image,
    "/images/shop-products/x7m-gameboy/1 player.jpg",
    "/images/shop-products/x7m-gameboy/2 player.jpg",
    "/images/shop-products/x7m-gameboy/2 player.jpeg",
    "/images/shop-products/x7m-gameboy/my-11134207-7qukz-levvhvc52s7od3.jpeg",
    "/images/shop-products/x7m-gameboy/my-11134207-7qul1-lk9chegn79it8f.jpeg",
    "/images/shop-products/x7m-gameboy/my-11134207-7qul1-lk9chegn8o39cd.jpeg",
    "/images/shop-products/x7m-gameboy/my-11134207-7qul4-levvhvc51dn8cd.jpeg",
    "/images/shop-products/x7m-gameboy/my-11134207-7qul4-levvhvc546s443.jpeg",
    "/images/shop-products/x7m-gameboy/my-11134207-7qul6-levvhvc5cmfpbf.jpeg",
    "/images/shop-products/x7m-gameboy/my-11134207-7qul8-levvhvc59tat01.jpeg"
  ];

  // 初始化当前图片
  useEffect(() => {
    setCurrentImage(gameboy.image);
  }, [gameboy.image]);

  // 相关产品 - 确保始终有4个产品
  const relatedProducts = products.filter(p => 
    p.name !== 'X7M Retro Handheld Game Console – SUP Style, 500 Classic Games, Big Screen, TV Output' && 
    (p.category === 'retro-gaming' || p.category === 'nintendo-switch' || p.category === 'accessories')
  );
  
  // 如果相关产品不足4个，用其他产品补充
  const getRelatedProducts = () => {
    if (relatedProducts.length >= 4) {
      return relatedProducts.slice(0, 4);
    }
    
    // 如果相关产品不足4个，添加其他产品
    const otherProducts = products.filter(p => 
      p.name !== 'X7M Retro Handheld Game Console – SUP Style, 500 Classic Games, Big Screen, TV Output' && 
      !relatedProducts.some(rp => rp.id === p.id)
    );
    
    return [...relatedProducts, ...otherProducts].slice(0, 4);
  };
  
  const displayProducts = getRelatedProducts();

  // 动态价格计算
  const getCurrentPrice = () => {
    const basePrice = selectedPlayer === 'Single Player' ? 34.90 : 37.90;
    return `S$${basePrice.toFixed(2)}`;
  };

  // 获取当前原价
  const getCurrentOriginalPrice = () => {
    const originalPrice = selectedPlayer === 'Single Player' ? 49.90 : 52.90;
    return `S$${originalPrice.toFixed(2)}`;
  };

  // 计算折扣百分比
  const getDiscountPercentage = () => {
    const currentPrice = selectedPlayer === 'Single Player' ? 34.90 : 37.90;
    const currentOriginalPrice = selectedPlayer === 'Single Player' ? 49.90 : 52.90;
    const discount = ((currentOriginalPrice - currentPrice) / currentOriginalPrice) * 100;
    return Math.round(discount);
  };

  // 处理添加到购物车
  const handleAddToCart = async () => {
    setIsAddingToCart(true);
    
    const productToAdd = {
      ...gameboy,
      selectedColor,
      selectedPlayer,
      price: getCurrentPrice(),
      quantity
    };
    
    addToCart(productToAdd);
    
    // 显示添加成功的反馈
    setTimeout(() => {
      setIsAddingToCart(false);
    }, 1000);
  };

  // 处理立即购买
  const handleBuyNow = () => {
    const productToAdd = {
      ...gameboy,
      selectedColor,
      selectedPlayer,
      price: getCurrentPrice(),
      quantity
    };
    
    addToCart(productToAdd);
    navigate('/cart');
  };

  return (
    <div className="container mx-auto px-4 py-12 text-white">
      {/* 面包屑导航 */}
      <div className="text-sm text-gray-400 mb-6">
        <Link to="/" className="hover:text-white">Home</Link> / 
        <Link to="/shop" className="hover:text-white mx-2">Shop</Link> / 
        <Link to="/shop/retro-gaming" className="hover:text-white mr-2">Retro Gaming</Link> / 
        <span className="text-white">{gameboy.name}</span>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        {/* 左侧产品图片 */}
        <div>
          <ProductImageGallery
            images={additionalImages}
            currentImage={currentImage}
            onImageChange={setCurrentImage}
          />
        </div>
        
        {/* 右侧产品信息 */}
        <div>
          <h1 className="text-3xl font-bold mb-2">{gameboy.name}</h1>
          
          {/* 评分 */}
          <div className="flex items-center mb-4">
            <div className="flex text-yellow-400">
              {[...Array(5)].map((_, i) => (
                <span key={i} className={i < Math.floor(gameboy.rating) ? "text-yellow-400" : "text-gray-600"}>★</span>
              ))}
            </div>
            <span className="ml-2 text-gray-400">({gameboy.reviews} reviews)</span>
          </div>
          
          {/* 价格 */}
          <div className="mb-6">
            <div className="flex items-center space-x-3 mb-2">
              <span className="text-3xl font-bold text-white">{getCurrentPrice()}</span>
              <span className="text-xl text-gray-400 line-through">{getCurrentOriginalPrice()}</span>
              <span className="bg-red-500 text-white px-2 py-1 rounded text-sm font-bold">
                -{getDiscountPercentage()}%
              </span>
            </div>
            <div className="text-sm text-gray-400">
              {selectedPlayer === 'Single Player' ? 'Single Player Version' : 'Double Players Version'}
            </div>
          </div>
          
          {/* 库存状态 */}
          <div className="mb-6">
            {gameboy.inStock ? (
              <span className="text-green-500 flex items-center">
                <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                In Stock
              </span>
            ) : (
              <span className="text-red-500 flex items-center">
                <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                Out of Stock
              </span>
            )}
          </div>
          
          {/* 简短描述 */}
          <p className="text-gray-300 mb-6">{gameboy.description}</p>
          
          {/* 选择颜色 */}
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3">Choose Colour</h3>
            <div className="flex flex-wrap gap-3">
              {[
                { 
                  name: 'Red & Blue', 
                  image: '/images/shop-products/x7m-gameboy/2 player.jpeg' 
                }
              ].map((colorOption) => (
                <div key={colorOption.name} className="flex flex-col items-center">
                  <button 
                    className={`border-2 ${colorOption.name === selectedColor ? 'border-green-500' : 'border-gray-700'} 
                      rounded-md overflow-hidden w-20 h-20 hover:border-green-500 transition-colors`}
                    onClick={() => setSelectedColor(colorOption.name)}
                  >
                    <img 
                      src={colorOption.image} 
                      alt={colorOption.name}
                      className="w-full h-full object-cover"
                    />
                  </button>
                  <span className="text-sm text-gray-300 mt-2 text-center">{colorOption.name}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* 选择玩家模式 */}
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3">Choose Player Mode</h3>
            <div className="flex gap-3">
              {[
                { 
                  name: 'Single Player', 
                  image: '/images/shop-products/x7m-gameboy/1 player.jpg'
                },
                { 
                  name: 'Double Players', 
                  image: '/images/shop-products/x7m-gameboy/2 player.jpg'
                }
              ].map((playerOption) => (
                <div key={playerOption.name} className="flex flex-col items-center">
                  <button 
                    className={`border-2 ${playerOption.name === selectedPlayer ? 'border-green-500' : 'border-gray-700'} 
                      rounded-md overflow-hidden w-20 h-20 hover:border-green-500 transition-colors`}
                    onClick={() => setSelectedPlayer(playerOption.name)}
                  >
                    <img 
                      src={playerOption.image} 
                      alt={playerOption.name}
                      className="w-full h-full object-cover"
                    />
                  </button>
                  <div className="text-center mt-2">
                    <div className="text-sm font-medium text-white">{playerOption.name}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Quantity */}
          <div className="mb-8">
            <h3 className="text-lg font-medium mb-3">Quantity</h3>
            <div className="flex items-center">
              <button 
                className="bg-gray-800 px-4 py-2 rounded-l-md"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
              >
                -
              </button>
              <input 
                type="text" 
                value={quantity} 
                readOnly
                className="bg-gray-800 text-center w-16 py-2 border-x border-gray-700"
              />
              <button 
                className="bg-gray-800 px-4 py-2 rounded-r-md"
                onClick={() => setQuantity(quantity + 1)}
              >
                +
              </button>
            </div>
          </div>
          
          {/* Purchase Buttons */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <button 
              onClick={handleAddToCart}
              disabled={isAddingToCart}
              className={`py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                isAddingToCart 
                  ? 'bg-gray-600 cursor-not-allowed' 
                  : 'bg-green-600 hover:bg-green-700'
              } text-white`}
            >
              {isAddingToCart ? 'Adding...' : 'Add to Cart'}
            </button>
            <button 
              onClick={handleBuyNow}
              className="bg-gray-800 hover:bg-gray-700 text-white py-2 px-4 rounded-md text-sm font-medium border border-gray-600 transition-colors"
            >
              Buy Now
            </button>
          </div>
          
          {/* Shipping Info */}
          <div className="border-t border-gray-700 pt-6">
            <div className="flex items-center mb-3">
              <svg className="w-5 h-5 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1v-5h2a1 1 0 00.9-.5l1.5-2A1 1 0 0015 7h-3V4a1 1 0 00-1-1H3zM14 7l-1.5 2H10V5h4v2z" />
              </svg>
              <span className="text-gray-300">Free Meet Up (Available at Sengkang & Orchard locations)</span>
            </div>
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
              <span className="text-gray-300">Available Shipping</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* 产品详情选项卡 */}
      <div className="mt-16">
        <div className="border-b border-gray-700 mb-8">
          <div className="flex space-x-8">
            <button 
              onClick={() => setActiveTab('overview')}
              className={`pb-4 px-2 font-medium ${activeTab === 'overview' ? 'text-white border-b-2 border-green-500' : 'text-gray-400'}`}
            >
              Overview
            </button>
            <button 
              onClick={() => setActiveTab('specs')}
              className={`pb-4 px-2 font-medium ${activeTab === 'specs' ? 'text-white border-b-2 border-green-500' : 'text-gray-400'}`}
            >
              Specifications
            </button>
            <button 
              onClick={() => setActiveTab('reviews')}
              className={`pb-4 px-2 font-medium ${activeTab === 'reviews' ? 'text-white border-b-2 border-green-500' : 'text-gray-400'}`}
            >
              Reviews ({gameboy.reviews})
            </button>
          </div>
        </div>
        
        {/* 选项卡内容 */}
        <div>
          {activeTab === 'overview' && (
            <div>
              <h2 className="text-2xl font-bold mb-6">Product Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div>
                  <p className="text-gray-300 mb-6">
                    The X7M Retro Handheld Game Console delivers the ultimate nostalgic gaming experience with 
                    its SUP-style design and 500 built-in classic FC games. This premium handheld device features 
                    a large 3.5-inch LCD display and TV output capability for the ultimate retro gaming setup.
                  </p>
                  <p className="text-gray-300 mb-6">
                    With single and double player modes, rechargeable battery, and compact design, you'll enjoy 
                    hours of classic gaming entertainment. The device's AV output allows you to connect to your 
                    TV for a bigger screen experience, perfect for sharing the fun with friends and family.
                  </p>
                  <p className="text-gray-300">
                    Perfect for both nostalgic gamers and newcomers to retro gaming, the X7M Retro Handheld 
                    Game Console offers the perfect blend of classic FC gameplay with modern convenience, 
                    making it the ideal choice for anyone who loves portable retro gaming.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-4">Key Features</h3>
                  <ul className="space-y-4">
                    {gameboy.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <svg className="w-6 h-6 mr-2 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <span className="font-medium">{feature}</span>
                          <p className="text-gray-400 text-sm mt-1">
                            {feature === "500 Classic FC Games" && "Massive collection of classic FC games built-in for endless entertainment"}
                            {feature === "3.5-inch LCD Display" && "Large, clear LCD display for the best retro gaming experience"}
                            {feature === "TV Output Support" && "Connect to your TV for bigger screen gaming with friends"}
                            {feature === "Dual Player Mode" && "Single and double player modes for shared gaming fun"}
                            {feature === "Rechargeable Battery" && "1020mAh lithium battery for hours of portable gaming"}
                            {feature === "Compact Design" && "Lightweight and portable design perfect for gaming on the go"}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'specs' && (
            <div>
              <h2 className="text-2xl font-bold mb-6">Technical Specifications</h2>
              <div className="bg-gray-800 rounded-lg p-6">
                <table className="w-full">
                  <tbody>
                    {Object.entries(gameboy.specs).map(([key, value], index) => (
                      <tr key={index} className={index !== 0 ? "border-t border-gray-700" : ""}>
                        <td className="py-4 pr-4 font-medium w-1/3">{key}</td>
                        <td className="py-4 text-gray-300">{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {activeTab === 'reviews' && (
            <div>
              <h2 className="text-2xl font-bold mb-6">Customer Reviews</h2>
              <div className="flex items-center mb-8">
                <div className="mr-4">
                  <div className="text-5xl font-bold">{gameboy.rating}</div>
                  <div className="flex text-yellow-400 mt-2">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className={i < Math.floor(gameboy.rating) ? "text-yellow-400" : "text-gray-600"}>★</span>
                    ))}
                  </div>
                  <div className="text-sm text-gray-400 mt-1">Based on {gameboy.reviews} reviews</div>
                </div>
                <div className="flex-1 ml-6">
                  {[5, 4, 3, 2, 1].map((star) => (
                    <div key={star} className="flex items-center mb-2">
                      <div className="w-12 text-sm text-gray-400">{star} stars</div>
                      <div className="w-full bg-gray-700 rounded-full h-2 mx-2">
                        <div 
                          className="bg-yellow-400 h-2 rounded-full" 
                          style={{ 
                            width: `${star === 5 ? 70 : star === 4 ? 20 : star === 3 ? 7 : star === 2 ? 2 : 1}%` 
                          }}
                        ></div>
                      </div>
                      <div className="w-12 text-sm text-right text-gray-400">
                        {star === 5 ? '70%' : star === 4 ? '20%' : star === 3 ? '7%' : star === 2 ? '2%' : '1%'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* 示例评论 */}
              <div className="space-y-6">
                {[
                  { name: "David L.", rating: 5, date: "1 week ago", comment: "This X7M Gameboy is absolutely fantastic! The 4-inch screen is crystal clear and the 500+ games keep me entertained for hours. The battery life is impressive and the WiFi feature is a nice bonus." },
                  { name: "Emma R.", rating: 5, date: "2 weeks ago", comment: "Perfect for retro gaming! The enhanced graphics make old games look amazing. The build quality is solid and it's comfortable to hold during long gaming sessions. Highly recommended!" },
                  { name: "Tom K.", rating: 4, date: "1 month ago", comment: "Great handheld gaming device with lots of games. The screen quality is excellent and the controls are responsive. The only minor issue is that some games take a moment to load, but overall it's a great product." }
                ].map((review, index) => (
                  <div key={index} className="border-b border-gray-700 pb-6">
                    <div className="flex justify-between mb-2">
                      <span className="font-medium">{review.name}</span>
                      <span className="text-gray-400 text-sm">{review.date}</span>
                    </div>
                    <div className="flex text-yellow-400 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className={i < review.rating ? "text-yellow-400" : "text-gray-600"}>★</span>
                      ))}
                    </div>
                    <p className="text-gray-300">{review.comment}</p>
                  </div>
                ))}
              </div>
              
              {/* 查看更多评论按钮 */}
              <div className="mt-8 text-center">
                <button className="bg-gray-800 hover:bg-gray-700 text-white py-3 px-6 rounded-md font-medium">
                  Load More Reviews
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* 相关产品 */}
      <div className="mt-20">
        <h2 className="text-2xl font-bold mb-8">You May Also Like</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {displayProducts.map(product => (
            <div key={product.id} className="bg-gray-800 rounded-lg overflow-hidden hover:bg-gray-700 transition-colors cursor-pointer" onClick={() => navigate(`/product/${product.id}`)}>
              <img src={product.image} alt={product.name} className="w-full h-48 object-cover" />
              <div className="p-4">
                <h3 className="font-medium mb-2">{product.name}</h3>
                <div className="flex justify-between items-center">
                  <span className="font-bold">{product.price}</span>
                  <button 
                    className="bg-green-600 hover:bg-green-700 text-white py-1 px-2 rounded text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/product/${product.id}`);
                    }}
                  >
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default X7MGameboy;